import { AlertTriangle, Check, Database, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchJson, sendJson } from "../../api/rest";
import type { NodeSample, PreviewResult } from "../../types/flows";

// Writing an expression used to be guesswork: the only way to learn what
// `{{ item.llm }}` resolved to was to run the flow, and a notification node
// whose message rendered empty said nothing at all. These read the last run's
// real data, and the preview evaluates on the server through the same code the
// runner uses — so what it shows is what the node will actually send.

export const useNodeSample = (flowId: number | null, nodeId: string | null) => {
	const [sample, setSample] = useState<NodeSample | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!flowId || !nodeId) {
			setSample(null);
			return;
		}
		let cancelled = false;
		setLoading(true);
		fetchJson<NodeSample>(`/flows/${flowId}/sample?nodeId=${nodeId}`)
			.then((result) => {
				if (!cancelled) setSample(result);
			})
			.catch(() => {
				if (!cancelled) setSample(null);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [flowId, nodeId]);

	return { sample, loading };
};

const TYPE_TONE: Record<string, string> = {
	string: "text-emerald-600",
	number: "text-sky-600",
	boolean: "text-violet-600",
	array: "text-amber-600",
	object: "text-slate-400",
	null: "text-slate-400",
};

/** The fields the items actually carry, from the most recent run. */
export const SampleFields = ({
	sample,
	loading,
	onPick,
}: {
	sample: NodeSample | null;
	loading: boolean;
	onPick: (token: string) => void;
}) => {
	const [open, setOpen] = useState(false);

	if (loading) {
		return (
			<p className="flex items-center gap-1.5 text-[11px] text-slate-400">
				<Loader2 className="h-3 w-3 animate-spin" /> Looking at the last run…
			</p>
		);
	}
	if (!sample) return null;

	if (sample.note) {
		return (
			<p className="rounded-lg bg-slate-50 px-2.5 py-2 text-[11px] text-slate-500">
				{sample.note}
			</p>
		);
	}

	return (
		<div className="rounded-lg border border-slate-200">
			<button
				type="button"
				onClick={() => setOpen((value) => !value)}
				className="flex w-full items-center gap-1.5 px-2.5 py-2 text-left text-[11px] font-medium text-slate-600 hover:bg-slate-50"
			>
				<Database className="h-3 w-3 shrink-0 text-slate-400" />
				{sample.fields.length} fields from run #{sample.runId}
				<span className="font-normal text-slate-400">
					· {sample.itemCount} item{sample.itemCount === 1 ? "" : "s"}
					{sample.source === "output" ? " this node produced" : " coming in"}
				</span>
				<span className="ml-auto text-slate-400">{open ? "Hide" : "Show"}</span>
			</button>

			{open && (
				<div className="max-h-56 overflow-y-auto border-t border-slate-100 p-1">
					{sample.fields.map((field) => (
						<button
							key={field.path}
							type="button"
							onClick={() => onPick(`{{ item.${field.path} }}`)}
							title={`Insert {{ item.${field.path} }}`}
							className="flex w-full items-baseline gap-2 rounded px-1.5 py-1 text-left hover:bg-slate-50"
						>
							<span className="font-mono text-[11px] text-slate-700">
								item.{field.path}
							</span>
							<span
								className={`font-mono text-[10px] ${TYPE_TONE[field.type]}`}
							>
								{field.type}
							</span>
							<span className="ml-auto max-w-40 truncate font-mono text-[10px] text-slate-400">
								{field.example}
							</span>
						</button>
					))}
				</div>
			)}
		</div>
	);
};

/** What this template renders to against that same data. */
export const ExpressionPreview = ({
	flowId,
	nodeId,
	template,
}: {
	flowId: number | null;
	nodeId: string | null;
	template: string;
}) => {
	const [result, setResult] = useState<PreviewResult | null>(null);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const run = useCallback(
		(value: string) => {
			if (!flowId || !nodeId || !value.includes("{{")) {
				setResult(null);
				return;
			}
			sendJson<PreviewResult>(`/flows/${flowId}/preview`, "POST", {
				nodeId,
				template: value,
			})
				.then(setResult)
				.catch(() => setResult(null));
		},
		[flowId, nodeId],
	);

	// Debounced: this runs on every keystroke and each call is a database read.
	useEffect(() => {
		if (timer.current) clearTimeout(timer.current);
		timer.current = setTimeout(() => run(template), 350);
		return () => {
			if (timer.current) clearTimeout(timer.current);
		};
	}, [template, run]);

	const body = useMemo(() => {
		if (!result) return null;
		if (result.error) {
			return (
				<div className="flex items-start gap-1.5 text-danger">
					<AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
					<span>{result.error}</span>
				</div>
			);
		}
		if (result.warning) {
			return (
				<div className="flex items-start gap-1.5 text-warning-dark">
					<AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
					<span>{result.warning}</span>
				</div>
			);
		}
		return (
			<div className="flex items-start gap-1.5">
				<Check className="mt-0.5 h-3 w-3 shrink-0 text-success" />
				<span className="whitespace-pre-wrap break-words text-slate-600">
					{result.rendered}
				</span>
			</div>
		);
	}, [result]);

	if (!body) return null;

	return (
		<div className="rounded-lg bg-slate-50 px-2.5 py-2 font-mono text-[11px] leading-relaxed">
			<p className="mb-1 font-sans text-[10px] font-semibold uppercase tracking-wide text-slate-400">
				Renders as
			</p>
			{body}
		</div>
	);
};
